/* Revive: progressive enhancement. Commerce and contact submissions use Shopify. */
(() => {
  'use strict';
  document.documentElement.classList.replace('no-js', 'js');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const selectAll = (root, selector) => [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];
  const stories = new Set();
  let scrollFrame = 0;
  let toastTimer;
  const motionAllowed = () => document.body.dataset.motion !== 'false' && !reducedMotion.matches;
  const rootRoute = () => window.Shopify?.routes?.root || '/';

  const revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    for (const entry of entries) if (entry.isIntersecting) { entry.target.classList.remove('rv-reveal-wait'); revealObserver.unobserve(entry.target); }
  }, { threshold: 0.06, rootMargin: '0px 0px -20px 0px' }) : null;

  function initReveals(root) {
    selectAll(root, '[data-reveal]').forEach(el => {
      if (el.dataset.revealReady) return;
      el.dataset.revealReady = 'true';
      if (motionAllowed() && revealObserver && el.getBoundingClientRect().top > window.innerHeight) {
        el.classList.add('rv-reveal-wait');
        revealObserver.observe(el);
      }
    });
  }

  function setStoryChapter(section, index) {
    section.querySelectorAll('[data-story-panel]').forEach((panel, i) => {
      panel.classList.toggle('is-active', i === index);
      if (section.classList.contains('is-enhanced')) panel.setAttribute('aria-hidden', String(i !== index));
      else panel.removeAttribute('aria-hidden');
    });
    section.querySelectorAll('[data-story-step]').forEach((step, i) => step.classList.toggle('is-active', i === index));
  }

  function updateStories() {
    scrollFrame = 0;
    const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--rv-header-height')) || 80;
    for (const section of stories) {
      if (!section.isConnected) { stories.delete(section); continue; }
      if (!section.classList.contains('is-enhanced')) continue;
      const rect = section.getBoundingClientRect();
      const progress = Math.max(0, Math.min(1, (headerHeight - rect.top) / Math.max(1, rect.height - window.innerHeight + headerHeight)));
      const count = section.querySelectorAll('[data-story-panel]').length;
      const chapter = Math.min(count - 1, Math.floor(progress * count));
      if (section.dataset.activeChapter !== String(chapter)) {
        section.dataset.activeChapter = String(chapter);
        setStoryChapter(section, chapter);
      }
      const photo = section.querySelector('[data-story-image]');
      if (photo) photo.style.transform = `translate3d(0,${25 - progress * 50}px,0) rotate(${-10 + progress * 20}deg) scale(${0.91 + Math.sin(progress * Math.PI) * 0.1})`;
      section.style.setProperty('--story-progress', `${progress * 100}%`);
    }
  }

  function scheduleStories() { if (!scrollFrame) scrollFrame = requestAnimationFrame(updateStories); }
  function configureStories() {
    for (const section of stories) {
      const enhanced = motionAllowed() && window.innerHeight >= 650 && section.querySelectorAll('[data-story-panel]').length > 0;
      section.classList.toggle('is-enhanced', enhanced);
      delete section.dataset.activeChapter;
      if (!enhanced) {
        section.querySelector('[data-story-image]')?.style.removeProperty('transform');
        setStoryChapter(section, 0);
      }
    }
    scheduleStories();
  }

  function initHeader(root) {
    selectAll(root, '[data-header]').forEach(header => {
      if (header.dataset.initialized) return;
      header.dataset.initialized = 'true';
      const button = header.querySelector('[data-menu-toggle]');
      const menu = header.querySelector('[data-mobile-menu]');
      const close = () => { if (!menu || !button) return; menu.hidden = true; button.setAttribute('aria-expanded', 'false'); button.setAttribute('aria-label', 'Menü öffnen'); };
      if (button && menu) {
        button.addEventListener('click', () => {
          const open = button.getAttribute('aria-expanded') !== 'true';
          button.setAttribute('aria-expanded', String(open));
          button.setAttribute('aria-label', open ? 'Menü schließen' : 'Menü öffnen');
          menu.hidden = !open;
        });
        menu.addEventListener('click', event => { if (event.target.closest('a')) close(); });
        header.addEventListener('keydown', event => { if (event.key === 'Escape' && !menu.hidden) { close(); button.focus(); } });
      }
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(() => {
          const announcement = header.previousElementSibling?.classList.contains('rv-announcement') ? header.previousElementSibling.getBoundingClientRect().height : 0;
          const barHeight = header.querySelector('.rv-header-inner')?.getBoundingClientRect().height || 80;
          document.documentElement.style.setProperty('--rv-header-height', `${barHeight + announcement}px`);
          scheduleStories();
        });
        observer.observe(header);
      }
    });
  }

  function initRepair(root) {
    selectAll(root, '[data-repair-root]').forEach(container => {
      const form = container.querySelector('.rv-repair-form');
      if (!form || form.dataset.initialized || !form.querySelector('[data-form-step]')) return;
      form.dataset.initialized = 'true';
      const steps = [...form.querySelectorAll('[data-form-step]')];
      const indicators = [...form.querySelectorAll('[data-step-indicator]')];
      const next = form.querySelector('[data-step-next]');
      const back = form.querySelector('[data-step-back]');
      const submit = form.querySelector('[data-repair-submit]');
      const brand = form.querySelector('[data-repair-brand]');
      const model = form.querySelector('[data-repair-model]');
      const customWrap = form.querySelector('[data-custom-model]');
      const customInput = form.querySelector('[data-model-text]');
      const damageInputs = [...form.querySelectorAll('[data-damage]')];
      const description = form.querySelector('[data-repair-description]');
      const damageError = form.querySelector('[data-damage-error]');
      const modelOptions = [...model.options].map(option => ({ value: option.value, title: option.textContent, brand: option.dataset.brand }));
      let currentStep = 0;

      function showStep(index, focus = true) {
        currentStep = index;
        steps.forEach((step, i) => { step.hidden = i !== index; });
        indicators.forEach((indicator, i) => { indicator.classList.toggle('is-complete', i < index); if (i === index) indicator.setAttribute('aria-current', 'step'); else indicator.removeAttribute('aria-current'); });
        back.hidden = index === 0;
        next.hidden = index === steps.length - 1;
        submit.hidden = index !== steps.length - 1;
        if (focus) {
          const heading = steps[index].querySelector('legend');
          heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
          if (form.getBoundingClientRect().top < 70) form.scrollIntoView({ behavior: motionAllowed() ? 'smooth' : 'auto', block: 'start' });
        }
      }

      function updateSummary() {
        const custom = model.value === 'Anderes Modell';
        customWrap.hidden = !custom;
        customInput.disabled = !custom;
        customInput.required = custom;
        const modelText = custom ? customInput.value.trim() : model.value;
        container.querySelector('[data-summary-model]').textContent = modelText || 'Dein Handy. Wie neu.';
        const selected = damageInputs.filter(input => input.checked).map(input => input.dataset.damage);
        container.querySelector('[data-summary-damage]').textContent = selected.length ? selected.join(', ') : (description.value.trim() ? 'Individuelles Problem' : 'Noch nicht ausgewählt');
        if (selected.length || description.value.trim()) damageError.hidden = true;
      }

      function filterModels() {
        model.replaceChildren(new Option('Modell auswählen', ''));
        for (const option of modelOptions) if (option.value && (option.brand === brand.value || option.value === 'Anderes Modell')) model.add(new Option(option.title, option.value));
        model.disabled = !brand.value;
        if (brand.value === 'Andere') model.value = 'Anderes Modell';
        updateSummary();
      }

      function validateStep(index) {
        const invalid = [...steps[index].querySelectorAll('input,select,textarea')].find(input => !input.disabled && !input.checkValidity());
        if (invalid) { showStep(index, false); invalid.reportValidity(); return false; }
        if (index === 1 && !damageInputs.some(input => input.checked) && !description.value.trim()) {
          showStep(index, false);
          damageError.hidden = false;
          damageInputs[0]?.focus();
          return false;
        }
        return true;
      }

      brand.addEventListener('change', filterModels);
      model.addEventListener('change', updateSummary);
      form.addEventListener('input', updateSummary);
      next.addEventListener('click', () => { if (validateStep(currentStep)) showStep(currentStep + 1); });
      back.addEventListener('click', () => showStep(Math.max(0, currentStep - 1)));
      form.addEventListener('keydown', event => {
        if (event.key === 'Enter' && event.target.tagName !== 'TEXTAREA' && event.target.tagName !== 'BUTTON' && currentStep < steps.length - 1) {
          event.preventDefault();
          if (validateStep(currentStep)) showStep(currentStep + 1);
        }
      });
      submit.addEventListener('click', event => { for (let i = 0; i < steps.length; i++) if (!validateStep(i)) { event.preventDefault(); break; } });
      form.addEventListener('invalid', event => {
        const step = event.target.closest('[data-form-step]');
        if (step?.hidden) showStep(Number(step.dataset.formStep), false);
      }, true);
      // Leave the native Shopify contact POST, spam protection and success state intact.
      form.addEventListener('submit', event => { for (let i = 0; i < steps.length; i++) if (!validateStep(i)) { event.preventDefault(); return; } });
      filterModels();
      showStep(0, false);
    });
  }

  function showToast(message) {
    const toast = document.querySelector('[data-cart-toast]');
    if (!toast) return;
    toast.querySelector('[data-toast-text]').textContent = message;
    toast.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { toast.hidden = true; }, 9000);
  }

  async function refreshCartCount() {
    const response = await fetch(`${rootRoute()}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!response.ok) return;
    const cart = await response.json();
    document.querySelectorAll('[data-cart-count]').forEach(counter => { counter.textContent = String(cart.item_count); counter.hidden = cart.item_count === 0; counter.closest('a')?.setAttribute('aria-label', `Warenkorb, ${cart.item_count} Artikel`); });
  }

  function initProduct(root) {
    selectAll(root, '[data-product-section]').forEach(section => {
      if (section.dataset.initialized) return;
      section.dataset.initialized = 'true';
      let variantRequest;
      const originalButtonDisabled = section.querySelector('button[name="add"]')?.disabled;
      // Real links preserve server-side model selection without JavaScript.
      section.addEventListener('click', async event => {
        const selected = event.target.closest('[data-product-choice]');
        if (!selected || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const url = new URL(selected.href, window.location.origin);
        if (url.origin !== location.origin) return;
        const liveUrl = new URL(window.location.href);
        if (liveUrl.searchParams.has('preview_theme_id')) url.searchParams.set('preview_theme_id', liveUrl.searchParams.get('preview_theme_id'));
        event.preventDefault();
        if (section.querySelector('.rv-product-form')?.dataset.submitting) return;
        if (selected.getAttribute('aria-current') === 'true' && !variantRequest) return;
        variantRequest?.abort();
        const controller = new AbortController();
        variantRequest = controller;
        const productPath = new URL(section.dataset.productUrl, location.origin).pathname;
        if (url.pathname !== productPath) { window.location.assign(url.toString()); return; }
        const historyUrl = new URL(url);
        url.hash = '';
        url.searchParams.set('section_id', section.dataset.sectionId);
        const addButton = section.querySelector('button[name="add"]');
        const quantity = section.querySelector('[name="quantity"]')?.value;
        const message = section.querySelector('[data-product-error]');
        if (message) message.hidden = true;
        section.querySelectorAll('[data-product-choice]').forEach(choice => { delete choice.dataset.pending; });
        selected.dataset.pending = 'true';
        const status = section.querySelector('[data-option-status]');
        if (status) status.textContent = 'Deine Auswahl wird aktualisiert …';
        if (addButton) addButton.disabled = true;
        section.dataset.loading = 'true';
        section.setAttribute('aria-busy', 'true');
        try {
          const response = await fetch(url.toString(), { signal: controller.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Variante konnte nicht geladen werden.');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const replacement = html.querySelector('[data-product-section]');
          if (!replacement) throw new Error('Variante konnte nicht geladen werden.');
          if (controller.signal.aborted) return;
          const nextQuantity = replacement.querySelector('[name="quantity"]');
          if (nextQuantity && quantity) {
            const defaultQuantity = nextQuantity.value;
            nextQuantity.value = quantity;
            if (!nextQuantity.checkValidity()) nextQuantity.value = defaultQuantity;
          }
          section.replaceWith(replacement);
          initProduct(replacement);
          history.replaceState({}, '', historyUrl);
          const focusTarget = replacement.querySelector(`[data-option-position="${selected.dataset.optionPosition}"][data-option-value="${selected.dataset.optionValue}"]`);
          focusTarget?.focus({ preventScroll: true });
        } catch (error) {
          if (error.name === 'AbortError') return;
          if (addButton) addButton.disabled = originalButtonDisabled;
          if (message) { message.textContent = 'Die Auswahl konnte nicht geladen werden. Bitte versuche es noch einmal.'; message.hidden = false; }
        } finally {
          if (!controller.signal.aborted) {
            section.removeAttribute('aria-busy'); delete section.dataset.loading;
            delete selected.dataset.pending;
            if (status) status.textContent = '';
            variantRequest = null;
          }
        }
      });

      section.querySelectorAll('[data-gallery-image]').forEach(thumbnail => thumbnail.addEventListener('click', event => {
        const main = section.querySelector('.rv-main-media');
        if (!main) return;
        event.preventDefault();
        const image = new Image();
        image.alt = thumbnail.dataset.imageAlt || '';
        image.src = thumbnail.dataset.galleryImage;
        image.width = 1600;
        image.height = 1600;
        main.replaceChildren(image);
      }));

      const form = section.querySelector('.rv-product-form');
      form?.addEventListener('submit', async event => {
        event.preventDefault();
        if (section.dataset.loading || form.dataset.submitting) return;
        if (!form.reportValidity()) return;
        const addButton = form.querySelector('button[name="add"]');
        if (addButton?.disabled) return;
        const errorBox = section.querySelector('[data-product-error]');
        const originalLabel = addButton.innerHTML;
        errorBox.hidden = true;
        addButton.disabled = true;
        addButton.textContent = 'Wird hinzugefügt …';
        form.dataset.submitting = 'true';
        let added = false;
        try {
          const response = await fetch(`${rootRoute()}cart/add.js`, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' }, credentials: 'same-origin' });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : 'Das Produkt konnte nicht hinzugefügt werden.');
          added = true;
          showToast('Zu deinem Warenkorb hinzugefügt.');
          try { await refreshCartCount(); } catch { /* Addition succeeded; the cart itself stays authoritative. */ }
        } catch (error) {
          errorBox.textContent = error.message && !(error instanceof TypeError) ? error.message : 'Keine sichere Bestätigung erhalten. Bitte prüfe deinen Warenkorb, bevor du es erneut versuchst.';
          errorBox.hidden = false;
        } finally {
          addButton.innerHTML = originalLabel;
          addButton.disabled = false;
          delete form.dataset.submitting;
          if (added) addButton.focus({ preventScroll: true });
        }
      });
    });
  }

  function initialize(root = document) {
    initHeader(root);
    initReveals(root);
    initRepair(root);
    initProduct(root);
    selectAll(root, '[data-scroll-story]').forEach(section => stories.add(section));
    configureStories();
    selectAll(root, '[data-auto-submit]').forEach(select => { if (!select.dataset.initialized) { select.dataset.initialized = 'true'; select.addEventListener('change', () => select.form?.requestSubmit()); } });
  }

  document.querySelector('[data-toast-close]')?.addEventListener('click', () => { document.querySelector('[data-cart-toast]').hidden = true; });
  window.addEventListener('scroll', scheduleStories, { passive: true });
  window.addEventListener('resize', configureStories, { passive: true });
  reducedMotion.addEventListener('change', () => { if (reducedMotion.matches) document.querySelectorAll('.rv-reveal-wait').forEach(el => el.classList.remove('rv-reveal-wait')); configureStories(); });
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', () => scheduleStories());
  document.addEventListener('shopify:block:select', event => {
    const panel = event.target.closest('[data-story-panel]');
    const story = panel?.closest('[data-scroll-story]');
    if (!story?.classList.contains('is-enhanced')) return;
    const panels = [...story.querySelectorAll('[data-story-panel]')];
    const fraction = (panels.indexOf(panel) + 0.25) / panels.length;
    const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--rv-header-height')) || 80;
    const top = story.getBoundingClientRect().top + scrollY - headerHeight;
    window.scrollTo({ top: top + fraction * (story.offsetHeight - innerHeight + headerHeight), behavior: 'instant' });
  });
  initialize();
})();
